import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import { BUSINESS } from "../data/site";
import { Logo } from "./Logo";
import { useLanguage, usePath } from "../i18n/LanguageContext";
import { LANGS } from "../i18n/translations";

export const LangSwitcher = ({ className = "", testidPrefix = "lang" }) => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const switchTo = (code) => {
    const base = location.pathname.replace(/^\/(es|zh)(?=\/|$)/, "") || "/";
    const target = code === "en" ? base : `/${code}${base === "/" ? "" : base}`;
    navigate(target + location.search + location.hash);
  };

  return (
    <div className={`flex items-center border border-slate-300 bg-white ${className}`} data-testid={`${testidPrefix}-switcher`}>
      {LANGS.map((l) => (
        <button
          key={l.code}
          onClick={() => switchTo(l.code)}
          data-testid={`${testidPrefix}-${l.code}-button`}
          aria-label={`Switch language to ${l.label}`}
          className={`px-3 py-1.5 text-xs font-bold transition-colors ${
            lang === l.code ? "bg-slate-950 text-white" : "text-slate-600 hover:text-amber-700"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();
  const p = usePath();

  const NAV_LINKS = [
    { to: "/", label: t.nav.home, key: "home" },
    { to: "/products", label: t.nav.products, key: "products" },
    { to: "/services", label: t.nav.services, key: "services" },
    { to: "/about", label: t.nav.about, key: "about" },
    { to: "/contact", label: t.nav.contact, key: "contact" },
  ];

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <div className="bg-slate-950 text-slate-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-9 flex items-center justify-between text-xs">
          <div className="flex items-center gap-5">
            <a href={BUSINESS.phoneHref} className="flex items-center gap-1.5 hover:text-amber-400 transition-colors" data-testid="topbar-phone-link" aria-label="Call Pallet Works Logistic">
              <Phone className="h-3 w-3 text-amber-500" /> {BUSINESS.phone}
            </a>
            <a href={BUSINESS.emailHref} className="hidden sm:flex items-center gap-1.5 hover:text-amber-400 transition-colors" data-testid="topbar-email-link" aria-label="Email Pallet Works Logistic">
              <Mail className="h-3 w-3 text-amber-500" /> {BUSINESS.email}
            </a>
          </div>
          <div className="hidden md:flex items-center gap-1.5 font-mono2 tracking-widest uppercase text-[10px]">
            <Clock className="h-3 w-3 text-amber-500" /> {t.topbarHours}
          </div>
        </div>
      </div>

      <div className="bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 lg:h-[72px] flex items-center justify-between gap-4">
          <Link to={p("/")} className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link">
            <Logo className="h-10 w-10 lg:h-11 lg:w-11" testid="header-logo" />
            <span className="leading-none">
              <span className="block font-heading font-extrabold tracking-tight text-slate-900 text-lg lg:text-xl">PALLET WORKS</span>
              <span className="block font-mono2 text-[10px] tracking-[0.35em] text-amber-700 font-semibold mt-0.5">LOGISTIC</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={p(l.to)}
                data-testid={`nav-${l.key}-link`}
                className={({ isActive }) =>
                  `text-sm font-semibold whitespace-nowrap transition-colors hover:text-amber-700 ${
                    isActive ? "text-amber-700" : "text-slate-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <LangSwitcher />
            <a
              href={BUSINESS.phoneHref}
              data-testid="nav-call-now-button"
              className="px-5 py-2.5 text-sm font-bold whitespace-nowrap border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
            >
              {t.nav.callNow}
            </a>
            <Link
              to={p("/contact")}
              data-testid="nav-request-quote-button"
              className="px-5 py-2.5 text-sm font-bold whitespace-nowrap bg-amber-600 text-white hover:bg-amber-700 transition-colors flex items-center gap-2"
            >
              {t.nav.quote} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-slate-900"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle navigation menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
            <div className="px-4 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={p(l.to)}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.key}-link`}
                  className={({ isActive }) =>
                    `px-3 py-2.5 text-sm font-semibold ${isActive ? "text-amber-700 bg-amber-50" : "text-slate-700"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-3">
                <LangSwitcher testidPrefix="mobile-lang" className="w-fit" />
              </div>
              <div className="flex gap-2 mt-3">
                <a href={BUSINESS.phoneHref} data-testid="mobile-call-now-button" className="flex-1 text-center px-4 py-3 text-sm font-bold border-2 border-slate-900 text-slate-900">
                  {t.nav.callNow}
                </a>
                <Link to={p("/contact")} onClick={() => setOpen(false)} data-testid="mobile-request-quote-button" className="flex-1 text-center px-4 py-3 text-sm font-bold bg-amber-600 text-white">
                  {t.nav.quote}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
